/**
 * BOGO Cart Manager v2 - Buy 1 Get 1 Free
 *
 * FIXES in v2:
 * 1. Instant cart update — sync runs BEFORE returning the fetch response
 *    so the cart drawer re-renders with free items already present.
 * 2. Free item shows "FREE" / ₹0 — price is overridden in the DOM
 *    immediately after every sync, and a <style> tag is injected to
 *    hide the original price and replace it with a green "FREE" label.
 * 3. Duplicate-add guard — uses a queued promise so rapid clicks
 *    never add two free items.
 */

(function () {
  "use strict";

  const BOGO_FREE_PROP  = "_bogo_free";
  const BOGO_FREE_VALUE = "true";
  const BOGO_LABEL      = "🎁 FREE — Buy 1 Get 1";

  // ─── Promise queue — prevents race conditions ──────────────────────────────
  let syncQueue = Promise.resolve();
  function queueSync() {
    syncQueue = syncQueue.then(() => syncBogoItems()).catch(() => {});
    return syncQueue;
  }

  // ─── Shopify Cart API helpers ──────────────────────────────────────────────
  function cartFetch(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => r.json());
  }

  function getCart() {
    return fetch("/cart.js").then((r) => r.json());
  }

  // ─── Core sync ────────────────────────────────────────────────────────────
  async function syncBogoItems() {
    const config = window.BOGO_CONFIG;
    if (!config || !config.enabled || !config.rules?.length) return;

    const cart = await getCart();
    let changed = false;

    for (const rule of config.rules) {
      const paidId = String(rule.paidVariantId);
      const freeId = String(rule.freeVariantId);

      const paidItems = cart.items.filter(
        (i) => String(i.variant_id) === paidId && !i.properties[BOGO_FREE_PROP]
      );
      const freeItems = cart.items.filter(
        (i) =>
          String(i.variant_id) === freeId &&
          i.properties[BOGO_FREE_PROP] === BOGO_FREE_VALUE
      );

      const paidQty = paidItems.reduce((s, i) => s + i.quantity, 0);
      const freeQty = freeItems.reduce((s, i) => s + i.quantity, 0);

      if (paidQty === 0 && freeQty > 0) {
        // Remove all free items for this rule
        for (const fi of freeItems) {
          await cartFetch("/cart/change.js", { id: fi.key, quantity: 0 });
        }
        changed = true;

      } else if (paidQty > 0 && freeQty === 0) {
        // Add free item
        await cartFetch("/cart/add.js", {
          items: [{
            id: freeId,
            quantity: paidQty,
            properties: {
              [BOGO_FREE_PROP]: BOGO_FREE_VALUE,
              _bogo_label: BOGO_LABEL,
            },
          }],
        });
        changed = true;

      } else if (paidQty > 0 && freeQty !== paidQty) {
        // Sync quantity — update the first free line
        if (freeItems.length > 0) {
          await cartFetch("/cart/change.js", {
            id: freeItems[0].key,
            quantity: paidQty,
          });
          // Remove extras if somehow duplicated
          for (let i = 1; i < freeItems.length; i++) {
            await cartFetch("/cart/change.js", { id: freeItems[i].key, quantity: 0 });
          }
          changed = true;
        }
      }
    }

    if (changed) {
      document.dispatchEvent(new CustomEvent("bogo:cart-updated"));
      await refreshCartUI();
    }

    // Always patch prices in DOM after every sync
    patchFreePricesInDOM();
  }

  // ─── Instant DOM price patch ───────────────────────────────────────────────
  // Shopify cannot set price:0 via cart/add.js — it ignores the field.
  // Instead we inject CSS to hide the real price for free items and
  // replace it with a "FREE" label, then also walk the DOM directly.

  function injectBogoStyles() {
    if (document.getElementById("bogo-injected-styles")) return;
    const style = document.createElement("style");
    style.id = "bogo-injected-styles";
    style.textContent = `
      /* ── Hide original price on free BOGO items ── */

      /* Dawn / OS2 themes */
      .bogo-free-item .price-item,
      .bogo-free-item .cart-item__final-price,
      .bogo-free-item .totals__subtotal-value,
      .bogo-free-item dl.cart-item__discounts,
      /* Older themes */
      .bogo-free-item .cart__price,
      .bogo-free-item .cart-item__price-wrapper .price,
      .bogo-free-item .money {
        visibility: hidden !important;
        position: relative !important;
      }

      /* Inject FREE text via ::after on the price wrapper */
      .bogo-free-item .cart-item__price-wrapper::after,
      .bogo-free-item .cart__price-wrapper::after,
      .bogo-free-item .price-wrapper::after {
        content: 'FREE';
        visibility: visible !important;
        position: absolute;
        left: 0;
        top: 0;
        color: #16a34a;
        font-weight: 700;
        font-size: 14px;
        letter-spacing: 0.02em;
      }

      /* Total / subtotal column */
      .bogo-free-item .cart-item__totals .price,
      .bogo-free-item td.cart-item__totals span {
        visibility: hidden !important;
        position: relative !important;
      }
      .bogo-free-item .cart-item__totals::after {
        content: 'FREE';
        visibility: visible !important;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        color: #16a34a;
        font-weight: 700;
        font-size: 14px;
      }

      /* Hide internal _bogo_ properties from customer view */
      .bogo-free-item .product-option dt[data-label="_bogo_free"],
      .bogo-free-item .product-option dt[data-label="_bogo_label"],
      .bogo-free-item .cart-item__property[data-key="_bogo_free"],
      .bogo-free-item .cart-item__property[data-key="_bogo_label"] {
        display: none !important;
      }

      /* Lock quantity stepper on free items */
      .bogo-free-item .quantity__button,
      .bogo-free-item .quantity-selector__button,
      .bogo-free-item input.quantity__input,
      .bogo-free-item .cart-item__quantity-wrapper button {
        pointer-events: none !important;
        opacity: 0.4 !important;
      }

      /* Green highlight row */
      .bogo-free-item {
        background: #f0fdf4 !important;
        border-left: 3px solid #22c55e !important;
      }
    `;
    document.head.appendChild(style);
  }

  function patchFreePricesInDOM() {
    injectBogoStyles();

    // Walk all cart line items — identify free ones by data attribute or property text
    const allItems = document.querySelectorAll(
      ".cart-item, .cart__item, [data-cart-item], tr.cart-item"
    );

    allItems.forEach((el) => {
      const isFreeByAttr = el.dataset.bogoFree === "true";

      // Detect by property text
      const propText = el.textContent || "";
      const isFreeByProp =
        propText.includes("_bogo_free") ||
        propText.includes(BOGO_LABEL) ||
        el.querySelector('[data-key="_bogo_free"], [data-label="_bogo_free"]');

      if (isFreeByAttr || isFreeByProp) {
        el.classList.add("bogo-free-item");
        el.dataset.bogoFree = "true";

        // Add badge if missing
        if (!el.querySelector(".bogo-badge")) {
          const badge = document.createElement("span");
          badge.className = "bogo-badge";
          badge.innerHTML = "🎁 FREE GIFT";
          badge.style.cssText =
            "display:inline-flex;align-items:center;gap:4px;background:#16a34a;" +
            "color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:20px;" +
            "letter-spacing:0.05em;margin-bottom:4px;";
          el.prepend(badge);
        }

        // Directly zero out visible price nodes
        el.querySelectorAll(".price, .money, .cart-item__price, .price-item").forEach((node) => {
          // Skip the badge we just added
          if (node.closest(".bogo-badge")) return;
          node.style.visibility = "hidden";
          node.style.position = "relative";

          // Insert a FREE sibling if not already there
          if (!node.parentElement.querySelector(".bogo-free-label")) {
            const label = document.createElement("span");
            label.className = "bogo-free-label";
            label.textContent = "FREE";
            label.style.cssText =
              "color:#16a34a;font-weight:700;font-size:14px;position:absolute;left:0;top:0;";
            node.parentElement.style.position = "relative";
            node.parentElement.appendChild(label);
          }
        });
      }
    });
  }

  // ─── Cart UI refresh ───────────────────────────────────────────────────────
  async function refreshCartUI() {
    // 1. Fetch updated cart sections (Dawn, Sense, Refresh, Craft…)
    const sections = [
      "cart-drawer",
      "cart-icon-bubble",
      "cart-notification-product",
      "cart-notification-button",
      "main-cart-items",
      "main-cart-footer",
    ];

    try {
      const res = await fetch(
        `/cart?sections=${sections.join(",")}&t=${Date.now()}`
      );
      const data = await res.json();

      sections.forEach((id) => {
        if (!data[id]) return;
        // Replace section HTML in DOM
        const el =
          document.getElementById(id) ||
          document.querySelector(`[id="${id}"]`);
        if (el) {
          el.innerHTML = new DOMParser()
            .parseFromString(data[id], "text/html")
            .getElementById(id)?.innerHTML || el.innerHTML;
        }
      });
    } catch (_) {}

    // 2. Fire events for themes that listen
    ["cart:refresh", "cart:updated", "cart-drawer:refresh"].forEach((ev) => {
      document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
    });

    // 3. Update any badge counters
    try {
      const cart = await getCart();
      document
        .querySelectorAll(
          ".cart-count-bubble span, #CartCount, .cart__count, [data-cart-count], .cart-count"
        )
        .forEach((el) => (el.textContent = cart.item_count));
    } catch (_) {}

    // Re-run DOM patch after sections re-render
    setTimeout(patchFreePricesInDOM, 200);
    setTimeout(patchFreePricesInDOM, 800);
  }

  // ─── Intercept fetch (modern themes) ──────────────────────────────────────
  const _fetch = window.fetch;
  window.fetch = async function (...args) {
    const url =
      typeof args[0] === "string"
        ? args[0]
        : args[0]?.url ?? "";

    const isBogoInternal =
      url.includes("_bogo") || window._bogoSyncing;

    const isCartWrite =
      !isBogoInternal &&
      (url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update"));

    // Wait for the original call to complete first
    const response = await _fetch.apply(this, args);

    if (isCartWrite) {
      // Run sync AFTER the main call resolves but BEFORE returning
      // so themes that await fetch() get the updated cart
      await queueSync();
    }

    return response;
  };

  // ─── Intercept XHR (older/custom themes) ──────────────────────────────────
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._bogoUrl = url;
    return _open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    this.addEventListener("load", function () {
      const url = this._bogoUrl || "";
      if (
        !window._bogoSyncing &&
        (url.includes("/cart/add") || url.includes("/cart/change"))
      ) {
        queueSync();
      }
    });
    return _send.apply(this, arguments);
  };

  // ─── Observe DOM for cart re-renders ──────────────────────────────────────
  // Themes that swap in new HTML (cart drawer open, etc.) need a re-patch
  const observer = new MutationObserver(() => {
    clearTimeout(window._bogoPatchTimer);
    window._bogoPatchTimer = setTimeout(patchFreePricesInDOM, 150);
  });

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    injectBogoStyles();
    syncBogoItems();

    // Watch cart drawer / cart page for DOM changes
    const targets = [
      document.getElementById("cart-drawer"),
      document.getElementById("main-cart-items"),
      document.querySelector(".cart-drawer"),
      document.querySelector("cart-drawer"),
      document.body,
    ].filter(Boolean);

    targets.slice(0, 1).forEach((t) =>
      observer.observe(t, { childList: true, subtree: true })
    );

    // Fallback full-body observation if nothing more specific found
    if (targets[0] === document.body) {
      observer.observe(document.body, { childList: true, subtree: false });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Public API
  window.bogoSync = queueSync;
  window.bogoPatchPrices = patchFreePricesInDOM;
})();
